import {
    $applyNodeReplacement,
    type DOMConversionMap,
    type DOMConversionOutput,
    type DOMExportOutput,
    type EditorConfig,
    type NodeKey,
    type SerializedTextNode,
    type Spread,
    TextNode,
    LexicalEditor,
} from 'lexical';

import { i18next } from '@packlink/translation-provider';

export type SerializedTagNode = Spread<{ tag: string; tagKey: string }, SerializedTextNode>;
export type Tag = { key: string; translation: string; description: string };

const removeCurlyRegex = /\{\{([^{}]+)\}\}/g;

function $convertTagElement(domNode: HTMLElement): DOMConversionOutput | null {
    const textContent = domNode.textContent;

    if (textContent !== null) {
        const tagKey = textContent.replace(removeCurlyRegex, '$1');
        const node = $createTagNode({ key: tagKey, translation: i18next.t(`branded-emails.tag.${tagKey}`) });

        const hasBold = domNode.querySelector('b, strong') !== null;
        const hasItalic = domNode.querySelector('i, em') !== null;
        const hasUnderline = domNode.querySelector('u') !== null;

        hasBold && node.toggleFormat('bold');
        hasItalic && node.toggleFormat('italic');
        hasUnderline && node.toggleFormat('underline');

        return {
            node,
        };
    }

    return null;
}

export class TagNode extends TextNode {
    __tagText: string;
    __tagKey: string;

    constructor(tagText: string, tagKey: string, key?: NodeKey) {
        super(tagText, key);
        this.__tagText = tagText;
        this.__tagKey = tagKey;
    }

    static getType(): string {
        return 'tag';
    }

    static clone(node: TagNode): TagNode {
        return new TagNode(node.__tagText, node.__tagKey, node.__key);
    }

    static importJSON(serializedNode: SerializedTagNode): TagNode {
        return $createTagNode({ key: serializedNode.tagKey, translation: serializedNode.tag }).updateFromJSON(
            serializedNode,
        );
    }

    exportJSON(): SerializedTagNode {
        return {
            ...super.exportJSON(),
            tag: this.getTagText(),
            tagKey: this.getTagKey(),
        };
    }

    createDOM(config: EditorConfig): HTMLElement {
        const dom = super.createDOM(config);
        dom.classList.add('text-editor-tag');
        dom.spellcheck = false;
        return dom;
    }

    static importDOM(): DOMConversionMap | null {
        return {
            span: (domNode: HTMLElement) => {
                if (!domNode.hasAttribute('data-lexical-tag')) {
                    return null;
                }

                return {
                    conversion: $convertTagElement,
                    priority: 1,
                };
            },
        };
    }

    exportDOM(editor: LexicalEditor): DOMExportOutput {
        function getInnerElement(element: HTMLElement) {
            let deepestElement: HTMLElement = element;
            while (deepestElement.lastElementChild) {
                deepestElement = deepestElement.lastElementChild as HTMLElement;
            }
            return deepestElement;
        }

        let exportedElement: HTMLElement | null = null;
        const { element } = super.exportDOM(editor);
        const innerElement = getInnerElement(element as HTMLElement);
        innerElement.textContent = `{{${this.getTagKey()}}}`;

        if (element?.nodeName.toLowerCase() === 'span') {
            exportedElement = element as HTMLElement;
        } else {
            exportedElement = document.createElement('span');
            exportedElement.appendChild(element as HTMLElement);
        }

        exportedElement.setAttribute('data-lexical-tag', 'true');

        return { element: exportedElement };
    }

    isTextEntity(): true {
        return true;
    }

    canInsertTextBefore(): boolean {
        return false;
    }

    canInsertTextAfter(): boolean {
        return false;
    }

    getTagText(): string {
        // getLatest() ensures we are getting the most up-to-date value from the EditorState.
        const self = this.getLatest();
        return self.__tagText;
    }

    getTagKey(): string {
        // getLatest() ensures we are getting the most up-to-date value from the EditorState.
        const self = this.getLatest();
        return self.__tagKey;
    }
}

export function $createTagNode(tag: { key: string; translation: string }): TagNode {
    const node = new TagNode(tag.translation, tag.key);
    node.setMode('token').toggleDirectionless();
    return $applyNodeReplacement(node);
}
